/**
 * Client-side WAV PCM encoding.
 *
 * The backend's Azure integration accepts only WAV (PCM 16kHz mono 16-bit)
 * or true OGG/Opus (backend-nest/src/assessment/azure-speech.service.ts) —
 * everything else, including the WebM/Opus that MediaRecorder produces in
 * Chrome, is rejected outright as `audio_rejected`. This mirrors the exact
 * format the Android app writes (tooti-android/.../data/audio/WavRecorder.kt)
 * so both clients hit the same, already-proven path through the API.
 */

export const TARGET_SAMPLE_RATE = 16000

/**
 * Linear-interpolation resample to 16kHz.
 *
 * Adequate for speech (no anti-aliasing filter, but the drop from a typical
 * 44.1/48kHz mic capture to 16kHz is well above the Nyquist rate for speech
 * content) and needs no library — AudioContext's own sample rate is a
 * browser-chosen hardware default, not something `new AudioContext()` can
 * force to 16000 in most engines.
 */
export function resampleTo16kHz(samples, inputSampleRate) {
  if (inputSampleRate === TARGET_SAMPLE_RATE) return samples

  const ratio = inputSampleRate / TARGET_SAMPLE_RATE
  const outputLength = Math.max(1, Math.round(samples.length / ratio))
  const output = new Float32Array(outputLength)

  for (let i = 0; i < outputLength; i += 1) {
    const sourceIndex = i * ratio
    const indexBefore = Math.floor(sourceIndex)
    const indexAfter = Math.min(indexBefore + 1, samples.length - 1)
    const fraction = sourceIndex - indexBefore
    output[i] = samples[indexBefore] + (samples[indexAfter] - samples[indexBefore]) * fraction
  }

  return output
}

/** Concatenate the per-quantum chunks the recorder worklet posts over time. */
export function concatFloat32(chunks) {
  const totalLength = chunks.reduce((sum, chunk) => sum + chunk.length, 0)
  const result = new Float32Array(totalLength)
  let offset = 0
  for (const chunk of chunks) {
    result.set(chunk, offset)
    offset += chunk.length
  }
  return result
}

/**
 * Float32 [-1, 1] samples at 16kHz mono → a canonical 44-byte-header WAV
 * ArrayBuffer. Byte layout matches the Android app's WavRecorder exactly
 * (pinned there by WavHeaderTest.kt): RIFF/WAVE/fmt /data, PCM, mono,
 * 16000Hz, 16-bit, byte rate 32000, block align 2.
 */
export function encodeWav16kMono(samples) {
  const bitsPerSample = 16
  const blockAlign = bitsPerSample / 8 // mono, so this is just bytesPerSample
  const byteRate = TARGET_SAMPLE_RATE * blockAlign
  const dataSize = samples.length * blockAlign
  const buffer = new ArrayBuffer(44 + dataSize)
  const view = new DataView(buffer)

  writeAscii(view, 0, 'RIFF')
  view.setUint32(4, 36 + dataSize, true)
  writeAscii(view, 8, 'WAVE')
  writeAscii(view, 12, 'fmt ')
  view.setUint32(16, 16, true) // fmt chunk size
  view.setUint16(20, 1, true) // audio format: PCM
  view.setUint16(22, 1, true) // channel count: mono
  view.setUint32(24, TARGET_SAMPLE_RATE, true)
  view.setUint32(28, byteRate, true)
  view.setUint16(32, blockAlign, true)
  view.setUint16(34, bitsPerSample, true)
  writeAscii(view, 36, 'data')
  view.setUint32(40, dataSize, true)

  let offset = 44
  for (let i = 0; i < samples.length; i += 1) {
    const clamped = Math.max(-1, Math.min(1, samples[i]))
    view.setInt16(offset, Math.round(clamped * (clamped < 0 ? 0x8000 : 0x7fff)), true)
    offset += 2
  }

  return buffer
}

function writeAscii(view, offset, text) {
  for (let i = 0; i < text.length; i += 1) view.setUint8(offset + i, text.charCodeAt(i))
}
